import React, { useState } from 'react';
import { Button, Modal, FormInput, FormNumberInput } from '@/components/UI';
import SearchSelect from '@/components/UI/SearchSelect';
import { trpc, trpcClient } from '@/api/trpc';
import type { EntryScorecardsRow } from '@/types/appRouter';
import { useQueryClient } from '@tanstack/react-query';
import { showToast } from '@/components/Layout/Toast';
import ParameterSecondaryIdentity from './ParameterSecondaryIdentity';
import { requireScorecardCandidateContextFeatureRef, toSelector } from './helpers';
import type {
    CanonicalParameterFeatureIdentity,
    ParameterDiscoveryMetric,
    ScorecardListItem,
    ScorecardRuleCandidate,
    ScorecardStrategyContext
} from './types';

interface AddRuleModalProps {
    candidate: ScorecardRuleCandidate | null;
    createNew: boolean;
    experimentId: number;
    filteredScorecards: ScorecardListItem[];
    getParameterDisplayLabel: (parameter: {
        path: string;
        canonicalFeature?: CanonicalParameterFeatureIdentity | null | undefined;
    }) => string;
    isOpen: boolean;
    onClose: () => void;
    onRuleAdded: (targetScorecardId: number) => void;
    optimizationMode: ParameterDiscoveryMetric;
    strategyContext: ScorecardStrategyContext | null;
    selectedDuration: string;
    selectedHorizon: number;
    selectedScorecardId: number | null;
    setCreateNew: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedScorecardId: React.Dispatch<React.SetStateAction<number | null>>;
}

// Simple sizing policy rows for inline create
type SizingBand = { minScore: number; maxScore: number; multiplier: number };

const AddRuleModal = ({
    candidate,
    createNew,
    experimentId,
    filteredScorecards,
    getParameterDisplayLabel,
    isOpen,
    onClose,
    onRuleAdded,
    optimizationMode,
    strategyContext,
    selectedDuration,
    selectedHorizon,
    selectedScorecardId,
    setCreateNew,
    setSelectedScorecardId
}: AddRuleModalProps) => {
    const queryClient = useQueryClient();
    const [newScorecardName, setNewScorecardName] = useState('');
    const [newAcceptanceScore, setNewAcceptanceScore] = useState<number | null>(1);
    const [newMinMatchedRules, setNewMinMatchedRules] = useState<number | null>(1);
    const [newBaselineExperimentId, setNewBaselineExperimentId] = useState<number | null>(null);

    const [sizingPolicy, setSizingPolicy] = useState<SizingBand[]>([]);
    const addSizing = () => setSizingPolicy((p) => [...p, { minScore: 0, maxScore: 100, multiplier: 1 }]);

    const updateSizing = (i: number, k: keyof SizingBand, v: number) =>
        setSizingPolicy((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

    const removeSizing = (i: number) => setSizingPolicy((p) => p.filter((_, idx) => idx !== i));

    const [submitPending, setSubmitPending] = useState(false);

    const handleCloseModal = () => {
        setNewScorecardName('');
        setNewAcceptanceScore(1);
        setNewMinMatchedRules(1);
        setSizingPolicy([]);
        setNewBaselineExperimentId(null);
        onClose();
    };

    const submitAddRule = async () => {
        if (!candidate) return;
        setSubmitPending(true);

        try {
            let targetScorecardId: number | null = selectedScorecardId;

            // Create on the fly
            if (createNew) {
                if (!strategyContext) throw new Error('Missing strategy context');
                if (!newScorecardName.trim()) throw new Error('Scorecard name is required');

                const acc = newAcceptanceScore;
                const min = newMinMatchedRules;

                if (acc === null || acc <= 0) throw new Error('Min predicted profit must be greater than 0');
                if (min === null || min < 0) throw new Error('Min matched rules must be 0 or greater');

                const created = await trpcClient.scorecards.create.mutate({
                    name: newScorecardName.trim(),
                    strategyId: strategyContext.strategyId,
                    experimentId,
                    baselineExperimentId: newBaselineExperimentId ?? experimentId,
                    duration: selectedDuration,
                    horizonSeconds: selectedHorizon,
                    targetMetric: optimizationMode === 'options' ? 'option' : 'stock',
                    minPredictedProfit: acc,
                    thresholds: { minMatchedRules: min },
                    sizingPolicy: sizingPolicy.length ? sizingPolicy : undefined
                });

                targetScorecardId = created.id;
            }

            if (!targetScorecardId) throw new Error('Select a scorecard');

            // Build selector and compute lifts
            const selector = toSelector(candidate.label, candidate.group.value);

            // Extract lift and sample size from the candidate group
            const stockLift = optimizationMode !== 'options' ? candidate.group.lift || 0 : 0;
            const optionLift = optimizationMode === 'options' ? candidate.group.lift || 0 : undefined;
            const sampleSize = candidate.group.count || 1;

            const contextFeatureRef = requireScorecardCandidateContextFeatureRef(candidate.scorecardCandidate, candidate.parameterPath);

            await trpcClient.scorecards.addRule.mutate({
                scorecardId: targetScorecardId,
                contextFeatureRef,
                selectorType: selector.type,
                selector,
                stockLift,
                optionLift,
                sampleSize
            });

            // Invalidate scorecards query to refresh the list
            onRuleAdded(targetScorecardId);

            // Invalidate rule status query to update UI indicators
            void queryClient.invalidateQueries({
                queryKey: trpc.scorecards.getRulesByScorecard.queryOptions({ scorecardId: targetScorecardId }).queryKey
            });

            showToast('Rule added to scorecard', 'success');
            handleCloseModal();
            // Avoid hard navigation here; user may add several rules quickly
        } catch (e: unknown) {
            const err = e as Error;
            showToast(err?.message || 'Failed to add rule', 'error');
        } finally {
            setSubmitPending(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            title={
                candidate
                    ? `Add Rule • ${getParameterDisplayLabel({
                          path: candidate.parameterPath,
                          canonicalFeature: candidate.canonicalFeature
                      })}`
                    : 'Add Rule'
            }
            onClose={handleCloseModal}
        >
            {!candidate ? (
                <div className="text-zinc-400">No selection.</div>
            ) : (
                <div className="space-y-4">
                    <div className="text-sm">
                        <div className="text-white font-medium">
                            {getParameterDisplayLabel({
                                path: candidate.parameterPath,
                                canonicalFeature: candidate.canonicalFeature
                            })}
                        </div>
                        <ParameterSecondaryIdentity
                            parameter={{
                                path: candidate.parameterPath,
                                canonicalFeature: candidate.canonicalFeature
                            }}
                        />
                    </div>
                    <div className="text-sm text-zinc-400">
                        Group: <span className="text-white font-medium">{candidate.label}</span>
                    </div>

                    {strategyContext ? (
                        <div className="space-y-2">
                            <div className="flex items-center gap-3">
                                <label className="text-sm text-zinc-300">
                                    <input checked={!createNew} className="mr-2" type="radio" onChange={() => setCreateNew(false)} />
                                    Select existing scorecard
                                </label>
                                <label className="text-sm text-zinc-300">
                                    <input checked={createNew} className="mr-2" type="radio" onChange={() => setCreateNew(true)} />
                                    Create new scorecard
                                </label>
                            </div>

                            {!createNew ? (
                                <SearchSelect
                                    items={filteredScorecards.map((s: EntryScorecardsRow) => ({
                                        label: s.name,
                                        meta: s.initializationStatus,
                                        name: s.name,
                                        value: s.id
                                    }))}
                                    label="Scorecard"
                                    mode="single"
                                    placeholder={
                                        filteredScorecards.length === 0 ? 'No matching scorecards - create new' : 'Choose a scorecard'
                                    }
                                    value={selectedScorecardId}
                                    onChange={setSelectedScorecardId}
                                />
                            ) : (
                                <div className="space-y-3">
                                    <FormInput
                                        label="Name"
                                        value={newScorecardName}
                                        onChange={(e) => setNewScorecardName(e.target.value)}
                                    />
                                    <div className="grid grid-cols-2 gap-3">
                                        <FormNumberInput
                                            label="Acceptance Score"
                                            min={0}
                                            step={0.1}
                                            value={newAcceptanceScore}
                                            onValueChange={setNewAcceptanceScore}
                                        />
                                        <FormNumberInput
                                            integer
                                            label="Min Matched Rules"
                                            min={0}
                                            value={newMinMatchedRules}
                                            onValueChange={setNewMinMatchedRules}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <FormNumberInput
                                            integer
                                            label="Baseline Experiment ID"
                                            min={1}
                                            placeholder={String(experimentId)}
                                            value={newBaselineExperimentId}
                                            onValueChange={setNewBaselineExperimentId}
                                        />
                                        <div className="text-xs text-zinc-400 flex items-center">
                                            Required; can be the same as the rules experiment.
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="text-white font-medium">Sizing Policy</div>
                                            <Button size="sm" variant="secondary" onClick={addSizing}>
                                                + Add Band
                                            </Button>
                                        </div>
                                        {sizingPolicy.length === 0 ? (
                                            <div className="text-zinc-500 text-sm">No bands configured</div>
                                        ) : (
                                            <div className="space-y-2">
                                                {sizingPolicy.map((band, idx) => (
                                                    <div className="grid grid-cols-7 gap-2 items-end" key={idx}>
                                                        <div className="col-span-2">
                                                            <FormNumberInput
                                                                label="Min Score"
                                                                step={0.1}
                                                                value={band.minScore}
                                                                onValueChange={(value) => {
                                                                    if (value === null) return;

                                                                    updateSizing(idx, 'minScore', value);
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="col-span-2">
                                                            <FormNumberInput
                                                                label="Max Score"
                                                                step={0.1}
                                                                value={band.maxScore}
                                                                onValueChange={(value) => {
                                                                    if (value === null) return;

                                                                    updateSizing(idx, 'maxScore', value);
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="col-span-2">
                                                            <FormNumberInput
                                                                label="Multiplier"
                                                                step={0.1}
                                                                value={band.multiplier}
                                                                onValueChange={(value) => {
                                                                    if (value === null) return;

                                                                    updateSizing(idx, 'multiplier', value);
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="col-span-1">
                                                            <Button size="sm" variant="danger" onClick={() => removeSizing(idx)}>
                                                                Remove
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-red-400 text-sm">Strategy info not available; cannot list scorecards.</div>
                    )}

                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button disabled={submitPending} variant="primary" onClick={submitAddRule}>
                            {submitPending ? 'Adding...' : 'Add Rule'}
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default AddRuleModal;
